import EntraSSOLoginButton from "#/components/buttons/EntraSSOLoginButton";
import { GoogleSignInButton } from "#/components/auth/GoogleSignInButton";
import { authApi } from "#/api/authApi";
import { useAuth } from "#/hook/useAuth";
import { useClient } from "#/hook/useClient";
import LanguageSelector from "#/components/ui/LanguageSelector";
import { SupportedAuthenticationMethod } from "#/types/client";
import {
  Button,
  Divider,
  PasswordInput,
  TextInput,
  UnstyledButton,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { useState } from "react";
import { Link } from "react-router-dom";
import { notifications } from "@mantine/notifications";
import { useTranslation } from "react-i18next";
import ForgotPasswordModal from "./components/ForgotPasswordModal";

export default function LoginPage() {
  const { t } = useTranslation();
  const { client } = useClient();
  const { login } = useAuth();

  const [opened, { open, close, toggle }] = useDisclosure(false);

  const [loading, setLoading] = useState(false);
  const [googleButtonLoaded, setGoogleButtonLoaded] = useState(false);
  const form = useForm({
    initialValues: {
      email: "",
      password: "",
    },
    validate: {
      email: (value) => (/^\S+@\S+$/.test(value) ? null : t("auth.register.validation.validEmail")),
      password: (value) =>
        value.length > 4 ? null : t("auth.register.validation.passwordMinLength"),
    },
  });

  const handleLogin = async () => {
    const result = form.validate();
    if (result.hasErrors) return;
    setLoading(true);
    try {
      await login(form.values);
      setLoading(false);
    } catch {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (token: string) => {
    setLoading(true);
    try {
      const response = await authApi.loginWithGoogle(token);
      if (response.data) {
        // Google login successful - navigate to dashboard
        window.location.href = "/";
      }
    } catch (err) {
      const error = err as { response?: { data?: { code?: string; message?: string } } };
      if (error.response?.data?.code === "ACCOUNT_LINKING_AVAILABLE") {
        notifications.show({
          title: t("auth.login.accountLinkingAvailable"),
          message: error.response?.data?.message || "Email already registered. Please link your account.",
          color: "yellow",
        });
      } else {
        notifications.show({
          title: t("auth.login.googleSignInFailed"),
          message: error.response?.data?.message || t("auth.login.failedToSignInWithGoogle"),
          color: "red",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-screen min-h-screen flex">
      <img
        src={"/login.jpg"}
        className="object-cover h-full w-full absolute z-0 top-0 left-0"
        alt="bg"
      />

      <div className="absolute top-4 right-4 z-20 h-11">
        <LanguageSelector />
      </div>

      <div className="w-full h-full absolute top-0 left-0 z-10 grid grid-cols-1 lg:grid-cols-2">
        <div className="hidden lg:flex w-full h-screen items-center justify-center">
          <div className="w-fit h-fit m-auto">
            <img
              src={client && client.logo ? client.logo : "/logo.svg"}
              alt="TMA Logo"
              className="w-full h-20 lg:h-32 relative object-contain"
            />
          </div>
        </div>
        <div className="w-full z-10 bg-white/70 backdrop-blur-xs flex flex-col items-center justify-center px-6 lg:px-20 py-8">
          <div className="w-full max-w-md">
            <div className="lg:hidden flex justify-center mb-8">
              <img
                src={client && client.logo ? client.logo : "/logo.svg"}
                alt="TMA Logo"
                className="h-12"
              />
            </div>

            <h1 className="text-2xl font-bold mb-2 text-gray-900">
              {t("auth.login.title")}
            </h1>
            <p className="text-gray-600 mb-6">
              {t("auth.login.subtitle")}
            </p>

            {import.meta.env.VITE_GOOGLE_CLIENT_ID && (
              <>
                <GoogleSignInButton
                  onSuccess={handleGoogleSuccess}
                  onLoad={() => setGoogleButtonLoaded(true)}
                  onError={() => {
                    notifications.show({
                      title: t("auth.login.googleSignInFailed"),
                      message: t("auth.login.failedToSignInWithGoogle"),
                      color: "red",
                    });
                  }}
                />
                {googleButtonLoaded && (
                  <Divider
                    my="md"
                    label={t("common.or")}
                    labelPosition="center"
                    classNames={{ label: "text-gray-600" }}
                  />
                )}
              </>
            )}

            <div className="flex flex-col gap-4">
              <TextInput
                label={t("auth.login.email")}
                placeholder={t("auth.login.emailPlaceholder")}
                type="email"
                required
                key={form.key("email")}
                {...form.getInputProps("email")}
                disabled={loading}
                classNames={{
                  input:
                    "h-10 border border-black/70 bg-white/30 placeholder:text-gray-600",
                }}
              />

              <PasswordInput
                label={t("auth.login.password")}
                placeholder={t("auth.login.passwordPlaceholder")}
                required
                key={form.key("password")}
                {...form.getInputProps("password")}
                disabled={loading}
                classNames={{
                  innerInput: "placeholder:text-gray-600",
                  input:
                    "h-10 border border-black/70 bg-white/30 placeholder:text-gray-600",
                }}
              />

              <div className="w-full flex justify-end">
                <UnstyledButton
                  classNames={{
                    root: "text-black text-xs font-bold underline",
                  }}
                  onClick={open}
                >
                  {t("auth.login.forgotPassword")}
                </UnstyledButton>
              </div>

              <Button
                fullWidth
                size="md"
                onClick={handleLogin}
                loading={loading}
                style={{
                  background: client?.color ?? "black",
                }}
              >
                {t("auth.login.signIn")}
              </Button>

              <div className="w-full text-center">
                <span className="text-sm text-gray-600">{t("auth.login.noAccount")} </span>
                <Link to="/register" className="text-sm font-bold text-black underline">
                  {t("auth.login.signUp")}
                </Link>
              </div>
            </div>

            {client?.supportedAuthenticationMethods &&
              [
                SupportedAuthenticationMethod.ALL,
                SupportedAuthenticationMethod.SSO,
              ].includes(client?.supportedAuthenticationMethods) && (
                <>
                  <Divider
                    my="lg"
                    label={t("common.or")}
                    labelPosition="center"
                    classNames={{ label: "text-gray-600" }}
                  />
                  <EntraSSOLoginButton />
                </>
              )}
          </div>
        </div>
      </div>

      <ForgotPasswordModal opened={opened} onToggle={toggle} onClose={close} />
    </div>
  );
}
